///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment } from "react";
import { useSelector } from "react-redux";

import Accordion from "../form-controls/Accordion";
import General from "../new-bid/General";
import Timeline from "../new-bid/Timeline";
import Financials from "../new-bid/Financials";
import Attachments from "../new-bid/Attachments";
import Truckload from "../new-bid/Truckload";
import LessthanTruckload from "../new-bid/LessthanTruckload";
import Intermodal from "../new-bid/Intermodal";
import Strategy from "../new-bid/Strategy";

import { mode, modeType } from "../../helpers/types";
import messages from "../../helpers/messages";

function ReviewBid({
  bid,
  setBidState,
  setModeDetails,
  removeError,
  validationData,
  validateShipper,
  isBidView,
  initialRfqName,
  role,
  summary,
  userDivision,
  validateStrategicQuantile
}) {
  const { featureFlags: { crmEnableImdlUi } } = useSelector(state => state);
  const modes = bid.modes;
  const hasVisibleStrategyModes = modes.some(
    m =>
      m.mode === mode.tl ||
      m.mode === mode.ltl ||
      (m.mode === mode.imdl && !crmEnableImdlUi)
  );

  return (
    <div>
      <Accordion title={messages.bidInformation.defaultMessage}>
        <div className="font-bold epsilon pb-4 pl-4">
          {messages.general.defaultMessage}
        </div>
        <General
          bid={bid}
          setBidState={setBidState}
          validationData={validationData.bidInformation}
          removeError={removeError}
          validateShipper={validateShipper}
          isBidView={isBidView}
          initialRfqName={initialRfqName}
          userOrBidDivision={isBidView ? summary?.division : userDivision}
        />
        <div className="font-bold epsilon pb-4 pl-4 pt-12">
          {messages.timeline.defaultMessage}
        </div>
        <Timeline
          bid={bid}
          setBidState={setBidState}
          validationData={validationData.bidInformation}
          removeError={removeError}
          userOrBidDivision={isBidView ? summary?.division : userDivision}
        />
        <div className="font-bold epsilon pb-4 pl-4 pt-12">
          {messages.financials.defaultMessage}
        </div>
        <Financials
          bid={bid}
          setBidState={setBidState}
          validationData={validationData.bidInformation}
          removeError={removeError}
          userOrBidDivision={isBidView ? summary?.division : userDivision}
        />
        {bid.files.length > 0 && (
          <>
            <div className="font-bold epsilon pb-4 pl-4 pt-12">
              {messages.attachments.defaultMessage}
            </div>
            <Attachments
              files={bid.files}
              setBidState={setBidState}
              showDragDropArea={false}
            />
          </>
        )}
      </Accordion>
      <Accordion title={messages.modeInformation.defaultMessage}>
        {modes.map(modeDetails => (
          <Fragment key={modeDetails.mode}>
            {modeDetails.mode === mode.tl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.truckload.defaultMessage}
                </div>
                <Truckload
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  removeError={removeError}
                  validationData={validationData.modeInformation.tl}
                  shipper={bid.shipper}
                  role={role}
                  summary={summary}
                  userOrBidDivision={
                    isBidView ? summary?.division : userDivision
                  }
                  bidScac={bid.bidScac}
                  isBidView={isBidView}
                />
              </>
            )}
            {modeDetails.mode === mode.ltl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.lessThanTruckload.defaultMessage}
                </div>
                <LessthanTruckload
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  removeError={removeError}
                  validationData={validationData.modeInformation.ltl}
                  role={role}
                  summary={summary}
                />
              </>
            )}
            {modeDetails.mode === mode.imdl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.intermodal.defaultMessage}
                </div>
                <Intermodal
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  removeError={removeError}
                  validationData={validationData.modeInformation.imdl}
                  shipper={bid.shipper}
                  role={role}
                  summary={summary}
                  bidScac={bid.bidScac}
                />
              </>
            )}
          </Fragment>
        ))}
      </Accordion>
      {hasVisibleStrategyModes && (
      <Accordion title={messages.bidStrategy.defaultMessage}>
        {modes.map(modeDetails => (
          <Fragment key={modeDetails.mode}>
            {modeDetails.mode === mode.tl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.truckload.defaultMessage}
                </div>
                <Strategy
                  type={modeType.tl}
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  validationData={validationData.bidStrategy.tl}
                  removeError={removeError}
                  bidType={bid.bidType}
                  userOrBidDivision={
                    isBidView ? summary?.division : userDivision
                  }
                  validateStrategicQuantile={validateStrategicQuantile}
                  bidScac={bid.bidScac}                  
                  isReview={true}
                />
              </>
            )}
            {modeDetails.mode === mode.ltl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.lessThanTruckload.defaultMessage}
                </div>
                <Strategy
                  type={modeType.ltl}
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  validationData={validationData.bidStrategy.ltl}
                  removeError={removeError}
                  bidScac={bid.bidScac}
                  isReview={true}
                />
              </>
            )}
            {!crmEnableImdlUi && modeDetails.mode === mode.imdl && (
              <>
                <div className="font-bold epsilon pb-4 pl-4 pt-4">
                  {messages.intermodal.defaultMessage}
                </div>
                <Strategy
                  type={modeType.imdl}
                  modeDetails={modeDetails}
                  setModeDetails={setModeDetails}
                  validationData={validationData.bidStrategy.imdl}
                  removeError={removeError}
                  bidScac={bid.bidScac}
                  isReview={true}
                />
              </>
            )}
          </Fragment>
        ))}
      </Accordion>
      )}
    </div>
  );
}

export default ReviewBid;
